import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { MetricsResponse, MetricsAgent } from '../../types/monitoring.types';
import { clearAuth, getStoredAccessToken, getStoredRefreshToken, updateAccessToken } from './authSlice';

// Types temporaires - seront remplacés par les types corrects
interface AuthState {
  token: string | null;
  refreshToken: string | null;
}

interface RootState {
  auth: AuthState;
}

export const prepareAuthHeaders = (headers: Headers, getState: () => unknown) => {
  const state = getState() as RootState;
  const token = state.auth?.token || getStoredAccessToken();

  if (token) {
    headers.set('authorization', `Bearer ${token}`);
  }

  headers.set('Content-Type', 'application/json');
  return headers;
};

// Configuration de base pour les requêtes
const baseQuery = fetchBaseQuery({
  baseUrl: '/api/v1',
  prepareHeaders: (headers, { getState }) => prepareAuthHeaders(headers, getState),
});

// Base query avec gestion automatique de l'authentification
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await baseQuery(args, api, extraOptions);

  // Si le token a expiré (401), on tente un refresh
  if (result.error && result.error.status === 401) {
    const state = api.getState() as RootState;
    const refreshToken = state.auth?.refreshToken || getStoredRefreshToken();

    if (refreshToken) {
      // Tentative de refresh du token
      const refreshResult = await baseQuery(
        {
          url: '/auth/token/refresh/',
          method: 'POST',
          body: { refresh: refreshToken },
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        const refreshData = refreshResult.data as { access?: string; refresh?: string };
        if (refreshData.access) {
          api.dispatch(updateAccessToken({
            access: refreshData.access,
            refresh: refreshData.refresh || refreshToken,
          }));
        }
        // Retry la requête originale avec le nouveau token
        result = await baseQuery(args, api, extraOptions);
      } else {
        // Si le refresh échoue, déconnecter l'utilisateur
        api.dispatch(clearAuth());
      }
    } else {
      // Pas de refresh token, déconnecter
      api.dispatch(clearAuth());
    }
  }

  return result;
};

// API slice principal
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User', 'Agent', 'Subscription', 'Chat', 'Message', 'Monitoring'],
  endpoints: (builder) => ({
    // Endpoints d'authentification
    getCurrentUser: builder.query<any, void>({
      query: () => '/auth/me/',
      providesTags: ['User'],
    }),

    // Endpoints des agents
    getAgents: builder.query<any[], { page?: number; size?: number; status?: string }>({
      query: (params) => ({
        url: '/agents',
        params,
      }),
      providesTags: ['Agent'],
    }),

    getAgent: builder.query<any, string>({
      query: (id) => `/agents/${id}`,
      providesTags: (result, error, id) => [{ type: 'Agent', id }],
    }),

    createAgent: builder.mutation<any, Partial<any>>({
      query: (agent) => ({
        url: '/agents',
        method: 'POST',
        body: agent,
      }),
      invalidatesTags: ['Agent'],
    }),

    updateAgent: builder.mutation<any, { id: string; data: Partial<any> }>({
      query: ({ id, data }) => ({
        url: `/agents/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Agent', id }],
    }),

    deleteAgent: builder.mutation<void, string>({
      query: (id) => ({
        url: `/agents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Agent'],
    }),

    // Endpoints des abonnements
    getSubscriptions: builder.query<any[], void>({
      query: () => '/subscriptions',
      providesTags: ['Subscription'],
    }),

    getSubscription: builder.query<any, string>({
      query: (id) => `/subscriptions/${id}`,
      providesTags: (result, error, id) => [{ type: 'Subscription', id }],
    }),

    // Endpoints de chat WhatsApp
    getChats: builder.query<any[], { agentId?: string; limit?: number }>({
      query: (params) => ({
        url: '/whatsapp/chats',
        params,
      }),
      transformResponse: (response: { chats?: any[] }) => response.chats || [],
      providesTags: ['Chat'],
    }),

    getMessages: builder.query<any[], { chatId: string; limit?: number; offset?: number }>({
      query: (params) => ({
        url: `/whatsapp/chats/${params.chatId}/messages`,
        params: { limit: params.limit, offset: params.offset },
      }),
      transformResponse: (response: { messages?: any[] }) => response.messages || [],
      providesTags: ['Message'],
    }),

    sendMessage: builder.mutation<any, { chatId: string; content: string; type?: string }>({
      query: ({ chatId, ...body }) => ({
        url: `/whatsapp/chats/${chatId}/messages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Message', 'Chat'],
    }),

    updateChatStatus: builder.mutation<any, { chatId: string; status: string }>({
      query: ({ chatId, status }) => ({
        url: `/whatsapp/chats/${chatId}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['Chat'],
    }),

    // Endpoints de surveillance et métriques
    getGlobalMetrics: builder.query<MetricsResponse, void>({
      query: () => '/metrics',
      providesTags: ['Monitoring'],
    }),

    getAgentMetrics: builder.query<MetricsAgent, string>({
      query: (agentId) => `/agents/${agentId}/metrics`,
      providesTags: (result, error, agentId) => [{ type: 'Monitoring', id: agentId }],
    }),

    // Endpoint existant pour la compatibilité
    getAgentMetricsLegacy: builder.query<any, { agentId: string; period?: string }>({
      query: ({ agentId, period = '24h' }) => ({
        url: `/agents/${agentId}/metrics`,
        params: { period },
      }),
    }),

    getSystemHealth: builder.query<any, void>({
      query: () => '/health',
    }),
  }),
});

// Export des hooks générés automatiquement
export const {
  useGetCurrentUserQuery,
  useGetAgentsQuery,
  useGetAgentQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useDeleteAgentMutation,
  useGetSubscriptionsQuery,
  useGetSubscriptionQuery,
  useGetChatsQuery,
  useGetMessagesQuery,
  useSendMessageMutation,
  useUpdateChatStatusMutation,
  useGetGlobalMetricsQuery,
  useGetAgentMetricsQuery,
  useGetAgentMetricsLegacyQuery,
  useGetSystemHealthQuery,
} = apiSlice;

export default apiSlice.reducer;
